import React from 'react'

import { Box, Grid } from '@factorialco/deprecated-design-system'
import { EntitySelect, EntitySelectEntity, Select } from '@factorialco/f0-react/dist/experimental'

import i18n from 'modules/core/lib/i18n'

import { formatJobCatalogRoles, getLevelsByRole } from './jobCatalogTreeUtils'

interface Props {
  serializedTree: string
  selectedRoleUuid?: string
  selectedRoleLevelUuid?: string
  onLevelSelect: (roleUuid?: string, roleLevelUuid?: string) => void
}

interface ComponentState {
  selectedRoleEntity: EntitySelectEntity | null
  selectedLevel: string | undefined
  isInitialized: boolean
  userHasInteracted: boolean
}

export const JobCatalogRoleSelector = ({
  serializedTree,
  selectedRoleUuid,
  selectedRoleLevelUuid,
  onLevelSelect,
}: Props) => {
  const [state, setState] = React.useState<ComponentState>({
    selectedRoleEntity: null,
    selectedLevel: undefined,
    isInitialized: false,
    userHasInteracted: false,
  })

  const parsedTree = React.useMemo(() => {
    if (!serializedTree.trim()) return null
    try {
      return JSON.parse(serializedTree.trim())
    } catch {
      return null
    }
  }, [serializedTree])

  const jobRoleOptions = React.useMemo(
    () => formatJobCatalogRoles(serializedTree),
    [serializedTree]
  )

  const findEntityByRoleUuid = React.useCallback(
    (roleUuid: string): EntitySelectEntity | null => {
      const group = jobRoleOptions.find((g) => g.subItems?.some((i) => i.subId === roleUuid))

      if (!group) return null
      const selectedItem = group.subItems?.find((i) => i.subId === roleUuid)

      if (!selectedItem) return null
      return { ...group, subItems: [selectedItem] }
    },
    [jobRoleOptions]
  )

  const currentRoleUuid = state.selectedRoleEntity?.subItems?.[0]?.subId

  const selectedRoleLevels = React.useMemo(() => {
    if (!currentRoleUuid || !parsedTree) return []
    return getLevelsByRole(parsedTree, String(currentRoleUuid))
  }, [currentRoleUuid, parsedTree])

  const levelOptions = React.useMemo(
    () => selectedRoleLevels.map((level) => ({ label: level.name, value: level.node_uuid })),
    [selectedRoleLevels]
  )

  const levelError = React.useMemo(() => {
    if (state.selectedRoleEntity && !state.selectedLevel && state.userHasInteracted) {
      return i18n.t('common.validations.empty')
    }
    return undefined
  }, [state.selectedRoleEntity, state.selectedLevel, state.userHasInteracted])

  const handleUserInteraction = React.useCallback((updates: Partial<ComponentState>) => {
    setState((prev) => ({ ...prev, userHasInteracted: true, ...updates }))
  }, [])

  const handleRoleSelect = React.useCallback(
    (selectedEntity: EntitySelectEntity | null) => {
      const roleNodeUuid = selectedEntity?.subItems?.[0]?.subId
      handleUserInteraction({
        selectedRoleEntity: selectedEntity,
        selectedLevel: undefined,
      })
      onLevelSelect(roleNodeUuid ? String(roleNodeUuid) : undefined, undefined)
    },
    [onLevelSelect, handleUserInteraction]
  )

  const handleLevelSelect = React.useCallback(
    (value: string | undefined) => {
      const roleNodeUuid = state.selectedRoleEntity?.subItems?.[0]?.subId
      handleUserInteraction({ selectedLevel: value })
      onLevelSelect(roleNodeUuid ? String(roleNodeUuid) : undefined, value || undefined)
    },
    [state.selectedRoleEntity, onLevelSelect, handleUserInteraction]
  )

  React.useEffect(() => {
    if (state.isInitialized || jobRoleOptions.length === 0) return

    const entity = selectedRoleUuid ? findEntityByRoleUuid(selectedRoleUuid) : null
    setState((prev) => ({
      ...prev,
      selectedRoleEntity: entity,
      isInitialized: true,
    }))
  }, [selectedRoleUuid, findEntityByRoleUuid, state.isInitialized, jobRoleOptions.length])

  React.useEffect(() => {
    if (
      !state.isInitialized ||
      state.userHasInteracted ||
      !selectedRoleLevelUuid ||
      selectedRoleLevels.length === 0
    ) {
      return
    }

    const levelExists = selectedRoleLevels.some(
      (level) => level.node_uuid === selectedRoleLevelUuid
    )
    if (levelExists) {
      setState((prev) => ({ ...prev, selectedLevel: selectedRoleLevelUuid }))
    }
  }, [selectedRoleLevelUuid, selectedRoleLevels, state.isInitialized, state.userHasInteracted])

  React.useEffect(() => {
    if (!state.selectedRoleEntity && state.selectedLevel) {
      setState((prev) => ({ ...prev, selectedLevel: undefined }))
    }
  }, [state.selectedRoleEntity, state.selectedLevel])

  React.useEffect(() => {
    if (!selectedRoleUuid && !state.userHasInteracted) {
      setState((prev) => ({
        ...prev,
        selectedRoleEntity: null,
        selectedLevel: undefined,
      }))
    }
  }, [selectedRoleUuid, state.userHasInteracted])
  return (
    <Grid variant="fixed">
      <Box gridColSpan={{ xxs: 1, xs: 6 }}>
        <Box width="full" gap="s8">
          <EntitySelect
            width={360}
            label={i18n.t('job_catalog.job_catalog_tree_selector.role.label')}
            searchPlaceholder={i18n.t(
              'job_catalog.job_catalog_tree_selector.role.search_placeholder'
            )}
            notFoundTitle={i18n.t('job_catalog.job_catalog_tree_selector.role.not_found_title')}
            notFoundSubtitle={i18n.t(
              'job_catalog.job_catalog_tree_selector.role.not_found_subtitle'
            )}
            entities={jobRoleOptions}
            groups={[]}
            selectedGroup="all"
            onSelect={handleRoleSelect}
            onItemExpandedChange={() => {}}
            onGroupChange={() => {}}
            selectedEntities={state.selectedRoleEntity ? [state.selectedRoleEntity] : []}
            selectedItemsCopy={i18n.t(
              'job_catalog.job_catalog_tree_selector.role.trigger_selected'
            )}
            placeholder={i18n.t('job_catalog.job_catalog_tree_selector.role.trigger_placeholder')}
            singleSelector
            hiddenAvatar
            size="md"
          />
        </Box>
      </Box>
      <Box gridColSpan={{ xxs: 1, xs: 6 }}>
        <Select
          label={i18n.t('job_catalog.job_catalog_tree_selector.level.label')}
          name="job_role_level"
          placeholder={i18n.t('job_catalog.job_catalog_tree_selector.level.placeholder')}
          key={`level-select-${selectedRoleUuid}-${selectedRoleLevelUuid}-${state.selectedLevel}`}
          onChange={handleLevelSelect}
          size="md"
          value={state.selectedLevel}
          options={levelOptions}
          disabled={!state.selectedRoleEntity || selectedRoleLevels.length === 0}
          error={levelError}
        />
      </Box>
    </Grid>
  )
}
